import React, { useCallback, useEffect, useState } from 'react';
import { ExternalLink, Loader, MapPin, Printer, RefreshCw, RotateCcw, Truck, X } from 'lucide-react';
import { velocityApi } from '../../services/api';
import {
  VELOCITY_LABEL_TTL_NOTE,
  canReturnOrderWithVelocity,
  hasVelocityBookingRole,
  isVelocityShipmentCancellable,
  pickActiveVelocityShipment,
  velocityErrorMessage,
  velocityTotalCharges,
  velocityTrackUrl,
} from '../../utils/velocity';
import VelocityStatusPill from './VelocityStatusPill';
import VelocityTrackingModal from './VelocityTrackingModal';
import VelocityCancelModal from './VelocityCancelModal';
import VelocityReturnModal from './VelocityReturnModal';

const money = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? `₹${n.toFixed(2)}` : null;
};

const Row = ({ label, children, isDark }) => (
  <div>
    <p className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{label}</p>
    <div className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>{children}</div>
  </div>
);

/**
 * Order detail ka "Velocity shipping" section.
 * Shipments backend se aate hain (GET /velocity/orders/{id}/shipments, newest first)
 * aur usi me returns bhi hote hain (direction: RETURN).
 */
const VelocityShipmentCard = ({ order, isDark = false, themeColor = '#111827', canManage = false, onOrderChanged }) => {
  const orderId = order?.id;
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trackOpen, setTrackOpen] = useState(false);
  const [cancelState, setCancelState] = useState({ open: false, reship: false });
  const [returnOpen, setReturnOpen] = useState(false);

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await velocityApi.getShipments(orderId);
      setShipments(Array.isArray(res) ? res : res?.data || []);
    } catch (err) {
      setError(velocityErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load]);

  const active = pickActiveVelocityShipment(shipments);
  const others = shipments.filter((s) => s !== active);
  const canBook = canManage && hasVelocityBookingRole();
  const showCancel = canBook && isVelocityShipmentCancellable(active);
  const showReturn = canBook && canReturnOrderWithVelocity(order?.status);
  const charges = active ? velocityTotalCharges(active.charges) : null;

  const afterChange = () => {
    load();
    onOrderChanged?.();
  };

  // Order detail page ke baaki sections jaisa hi look
  const cardCls = isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100';
  const btnCls = `px-3 py-1.5 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 border transition-colors ${
    isDark ? 'border-white/10 text-gray-200 hover:bg-white/10' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
  }`;

  return (
    <div className={`p-6 rounded-3xl shadow-xl border ${cardCls}`}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className={`text-sm font-black uppercase tracking-widest flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
          <Truck className="w-4 h-4" style={{ color: themeColor }} /> Velocity shipping
        </h3>
        <button type="button" className={btnCls} onClick={load} disabled={loading}>
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {loading && (
        <div className="py-6 flex items-center justify-center gap-2 text-gray-500 text-sm">
          <Loader className="w-4 h-4 animate-spin" /> Loading shipments…
        </div>
      )}

      {!loading && error && (
        <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
      )}

      {!loading && !error && !active && (
        <p className="text-sm text-gray-500">No Velocity shipment for this order yet.</p>
      )}

      {!loading && !error && active && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Row label="AWB" isDark={isDark}>
              <span className="font-mono">{active.awbCode || 'Pending'}</span>
            </Row>
            <Row label="Courier" isDark={isDark}>
              {active.courierName || '—'}
            </Row>
            <Row label="Status" isDark={isDark}>
              <VelocityStatusPill status={active.status} isDark={isDark} />
            </Row>
            <Row label="Charges" isDark={isDark}>
              {money(charges) || '—'}
            </Row>
          </div>

          {active.lastActivity && (
            <p className={`text-xs flex items-start gap-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{active.lastActivity}</span>
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {active.labelUrl && (
              <a href={active.labelUrl} target="_blank" rel="noopener noreferrer" className={btnCls} title={VELOCITY_LABEL_TTL_NOTE}>
                <Printer className="w-3.5 h-3.5" /> Print label
              </a>
            )}
            <button type="button" className={btnCls} onClick={() => setTrackOpen(true)} disabled={!active.awbCode}>
              <MapPin className="w-3.5 h-3.5" /> Track
            </button>
            {velocityTrackUrl(active) && (
              <a href={velocityTrackUrl(active)} target="_blank" rel="noopener noreferrer" className={btnCls}>
                <ExternalLink className="w-3.5 h-3.5" /> Courier site
              </a>
            )}
            {showCancel && (
              <>
                <button
                  type="button"
                  className={`${btnCls} text-red-600 border-red-200`}
                  onClick={() => setCancelState({ open: true, reship: false })}
                >
                  <X className="w-3.5 h-3.5" /> Cancel
                </button>
                <button
                  type="button"
                  className={btnCls}
                  onClick={() => setCancelState({ open: true, reship: true })}
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Cancel &amp; reship
                </button>
              </>
            )}
            {showReturn && (
              <button type="button" className={btnCls} onClick={() => setReturnOpen(true)}>
                <RotateCcw className="w-3.5 h-3.5" /> Create return
              </button>
            )}
          </div>

          {active.labelUrl && <p className="text-[11px] text-amber-600">{VELOCITY_LABEL_TTL_NOTE}</p>}

          {others.length > 0 && (
            <div className="pt-3 border-t border-dashed border-gray-200">
              <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Other shipments
              </p>
              <ul className="space-y-2">
                {others.map((s) => (
                  <li key={s.id} className="flex items-center gap-3 text-xs">
                    <span className={`px-2 py-0.5 rounded border text-[10px] font-bold ${isDark ? 'border-white/10 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
                      {s.direction === 'RETURN' ? 'RETURN' : 'FORWARD'}
                    </span>
                    <span className="font-mono">{s.awbCode || '—'}</span>
                    <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>{s.courierName || '—'}</span>
                    <VelocityStatusPill status={s.status} isDark={isDark} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <VelocityTrackingModal open={trackOpen} order={order} onClose={() => setTrackOpen(false)} onRefreshed={load} />
      <VelocityCancelModal
        open={cancelState.open}
        order={order}
        defaultReship={cancelState.reship}
        onClose={() => setCancelState({ open: false, reship: false })}
        onCancelled={afterChange}
      />
      <VelocityReturnModal open={returnOpen} order={order} onClose={() => setReturnOpen(false)} onCreated={afterChange} />
    </div>
  );
};

export default VelocityShipmentCard;
